import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Calendar, Globe, Stethoscope, HandHeart, GraduationCap } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
function CountUp({
  end,
  duration = 2000,
  prefix = '',
  suffix = ''
}: {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number;
    let animationFrame: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);
  return <span data-id="element-436">
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </span>;
}
export function HomePage() {
  const countries = ['🇬🇧', '🇵🇰', '🇺🇸', '🇨🇦', '🇦🇺', '🇦🇪', '🇸🇦', '🇩🇪', '🇫🇷', '🇳🇱'];
  return <>
      <SEO title="Home" description="Khyber Medical Association UK - Connecting medical professionals, supporting communities, and serving humanity. A UK-based alumni and professional association." data-id="element-437" />

      {/* Hero Section */}
      <div className="relative bg-gray-900 text-white overflow-hidden min-h-[70vh] md:min-h-[75vh] lg:min-h-[80vh] flex items-center" data-id="element-438">
        <div className="absolute inset-0" data-id="element-439">
          <img src="https://cdn.magicpatterns.com/uploads/rgrSJawmhW3zF59H7uaMNf/Khabar_medical_college.jpg" alt="Khyber Medical College campus" className="h-full w-full object-cover object-center" data-id="element-440" />
          <div className="absolute inset-0 bg-black/25" data-id="element-441" />
        </div>

        <div className="relative container mx-auto px-4 md:px-6 py-18 md:py-24" data-id="element-442">
          <FadeIn className="max-w-4xl mx-auto" data-id="element-443">
            {/* Glassmorphic Content Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-2xl" data-id="element-444">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-4 md:mb-6 text-white" style={{
              textShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
            }} data-id="element-445">
                Connecting Medical Professionals.
                <br data-id="element-446" />
                Supporting Communities.
              </h1>
              <p className="text-base md:text-lg text-white/95 mb-8 md:mb-10 leading-relaxed" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
            }} data-id="element-447">
                A UK-based alumni and professional association supporting
                healthcare, networking, and charitable work across Khyber
                Pakhtunkhwa and beyond.
              </p>

              {/* Primary CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-448">
                <Link to="/membership" className="w-full sm:w-auto" data-id="element-449">
                  <Button size="lg" variant="secondary" className="w-full min-h-[48px] text-base md:text-lg px-8" data-id="element-450">
                    Join Membership
                  </Button>
                </Link>
                <Link to="/charity" className="w-full sm:w-auto" data-id="element-451">
                  <Button size="lg" variant="white" className="w-full min-h-[48px] text-base md:text-lg px-8" data-id="element-452">
                    <Heart className="mr-2 h-5 w-5" data-id="element-453" />
                    Donate
                  </Button>
                </Link>
              </div>

              {/* Secondary CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 text-sm" data-id="element-454">
                <Link to="/charity" className="text-white/90 hover:text-white transition-colors underline underline-offset-4" data-id="element-455">
                  View Our Charity Work →
                </Link>
                <Link to="/events" className="text-white/90 hover:text-white transition-colors underline underline-offset-4" data-id="element-456">
                  Upcoming Events →
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Impact Counters */}
      <Section className="relative -mt-20 pt-0 z-10" data-id="element-457">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-458">
          <FadeIn delay={0} data-id="element-459">
            <Card className="border-none shadow-xl bg-white h-full" data-id="element-460">
              <CardContent className="flex flex-col items-center justify-center p-8 text-center" data-id="element-461">
                <div className="h-14 w-14 rounded-full bg-green-50 text-green-600 flex items-center justify-center mb-4" data-id="element-462">
                  <Heart className="h-7 w-7" data-id="element-463" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-464">
                  <CountUp end={20000} prefix="£" data-id="element-465" />
                </div>
                <div className="text-sm font-medium text-gray-500 uppercase tracking-wide" data-id="element-466">
                  Funds Raised
                </div>
              </CardContent>
            </Card>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-467">
            <Card className="border-none shadow-xl bg-white h-full" data-id="element-468">
              <CardContent className="flex flex-col items-center justify-center p-8 text-center" data-id="element-469">
                <div className="h-14 w-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-4" data-id="element-470">
                  <Users className="h-7 w-7" data-id="element-471" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-472">
                  <CountUp end={500} suffix="+" data-id="element-473" />
                </div>
                <div className="text-sm font-medium text-gray-500 uppercase tracking-wide" data-id="element-474">
                  Active Members
                </div>
              </CardContent>
            </Card>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-475">
            <Card className="border-none shadow-xl bg-white h-full" data-id="element-476">
              <CardContent className="flex flex-col items-center justify-center p-8 text-center" data-id="element-477">
                <div className="h-14 w-14 rounded-full bg-accent-50 text-accent-600 flex items-center justify-center mb-4" data-id="element-478">
                  <Globe className="h-7 w-7" data-id="element-479" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-480">10+</div>
                <div className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3" data-id="element-481">
                  Countries Represented
                </div>
                <div className="flex flex-wrap gap-1 justify-center" data-id="element-482">
                  {countries.map((flag, i) => <span key={i} className="text-2xl" data-id="element-483">
                      {flag}
                    </span>)}
                </div>
              </CardContent>
            </Card>
          </FadeIn>
        </div>
      </Section>

      {/* Intro Section - History & Purpose */}
      <Section background="white" data-id="element-484">
        <div className="max-w-4xl mx-auto" data-id="element-485">
          <FadeIn className="text-center mb-12" data-id="element-486">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-primary-900" data-id="element-487">
              Who We Are
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed space-y-4" data-id="element-488">
              <p data-id="element-489">
                The Khyber Medical Association UK brings together alumni of
                Khyber Medical College and other medical graduates of Khyber
                Pakhtunkhwa origin living in the United Kingdom. Founded on the
                principles of professional excellence and humanitarian service,
                we have grown into a vibrant community of healthcare
                professionals dedicated to making a difference.
              </p>
              <p data-id="element-490">
                Our focus areas include healthcare support, professional
                networking, humanitarian relief, and community engagement. We
                work to strengthen bonds among medical professionals while
                extending our expertise and resources to those who need it most.
              </p>
            </div>
          </FadeIn>

          {/* Important Statement */}
          <FadeIn delay={0.2} data-id="element-491">
            <div className="bg-accent-50 border-l-4 border-accent-600 p-6 rounded-r-lg" data-id="element-492">
              <p className="text-gray-800 font-medium text-center" data-id="element-493">
                The Association does not associate itself with any political,
                ethnic, or religious organisations.
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Link Blocks - Key Areas */}
      <Section background="gray" data-id="element-494">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-495">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-primary-900" data-id="element-496">
            What We Offer
          </h2>
          <p className="text-gray-600" data-id="element-497">
            Discover the benefits of membership, our charitable initiatives, and
            upcoming opportunities to connect.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-498">
          <FadeIn delay={0} data-id="element-499">
            <Link to="/membership" className="group" data-id="element-500">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-primary-200" data-id="element-501">
                <CardHeader className="text-center pb-4" data-id="element-502">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform" data-id="element-503">
                    <GraduationCap className="h-8 w-8" data-id="element-504" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-505">
                    Membership Benefits
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-506">
                  <CardDescription className="text-base mb-4" data-id="element-507">
                    Join a prestigious network of medical professionals. Access
                    exclusive events, mentorship programs, and professional
                    development opportunities.
                  </CardDescription>
                  <div className="text-primary-700 font-medium group-hover:underline inline-flex items-center" data-id="element-508">
                    Learn More{' '}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-509" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-510">
            <Link to="/charity" className="group" data-id="element-511">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-200" data-id="element-512">
                <CardHeader className="text-center pb-4" data-id="element-513">
                  <div className="h-16 w-16 rounded-full bg-green-100 text-green-700 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform" data-id="element-514">
                    <HandHeart className="h-8 w-8" data-id="element-515" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-516">Charity Projects</CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-517">
                  <CardDescription className="text-base mb-4" data-id="element-518">
                    From flood relief to medical camps, we bring healthcare and
                    hope to underserved communities in Pakistan and beyond.
                  </CardDescription>
                  <div className="text-green-700 font-medium group-hover:underline inline-flex items-center" data-id="element-519">
                    View Projects{' '}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-520" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-521">
            <Link to="/events" className="group" data-id="element-522">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-accent-200" data-id="element-523">
                <CardHeader className="text-center pb-4" data-id="element-524">
                  <div className="h-16 w-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform" data-id="element-525">
                    <Calendar className="h-8 w-8" data-id="element-526" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-527">Events</CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-528">
                  <CardDescription className="text-base mb-4" data-id="element-529">
                    Connect with colleagues at our annual gala, educational
                    seminars, social gatherings, and professional networking
                    events.
                  </CardDescription>
                  <div className="text-accent-700 font-medium group-hover:underline inline-flex items-center" data-id="element-530">
                    See Calendar{' '}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-531" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>
        </div>
      </Section>

      {/* Recent Activity Preview */}
      <Section data-id="element-532">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-533">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-primary-900" data-id="element-534">
            Recent Impact
          </h2>
          <p className="text-gray-600" data-id="element-535">
            See how our members are making a tangible difference in communities
            across Pakistan and the UK.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-536">
          {[{
          title: 'Flood Relief Mission 2023',
          image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          desc: 'Emergency medical aid and supplies delivered to flood-affected regions in KPK.'
        }, {
          title: 'Free Eye Camp',
          image: 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          desc: 'Over 200 cataract surgeries performed for underserved communities.'
        }, {
          title: 'Medical Education Fund',
          image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          desc: 'Scholarships awarded to deserving medical students from KPK.'
        }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-537">
              <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow" data-id="element-538">
                <div className="h-48 overflow-hidden" data-id="element-539">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-540" />
                </div>
                <CardContent className="flex-1 p-6" data-id="element-541">
                  <h3 className="text-xl font-serif font-bold mb-2 text-primary-900" data-id="element-542">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm" data-id="element-543">{item.desc}</p>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="primary" className="text-center" data-id="element-544">
        <FadeIn data-id="element-545">
          <Stethoscope className="h-12 w-12 mx-auto mb-6 text-primary-200" data-id="element-546" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6 text-white" data-id="element-547">
            Join Our Mission
          </h2>
          <p className="text-primary-100 mb-8 max-w-2xl mx-auto text-lg" data-id="element-548">
            Whether you're looking to connect with fellow professionals, give
            back to communities in need, or advance your career, KMA UK welcomes
            you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-549">
            <Link to="/membership" data-id="element-550">
              <Button size="lg" variant="secondary" data-id="element-551">
                Become a Member
              </Button>
            </Link>
            <Link to="/contact" data-id="element-552">
              <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10 hover:text-white" data-id="element-553">
                Get in Touch
              </Button>
            </Link>
          </div>
        </FadeIn>
      </Section>
    </>;
}